import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  isLscCfbDynastySetupEnabled,
  isLscCfbVisionScheduleIntakeEnabled,
  isLscCfbVodScheduleIntakeEnabled,
} from '../../config/featureFlags';
import {
  approveCfbScheduleGame,
  createCfbScheduleGame,
  deleteCfbScheduleGame,
  getCfbSchedule,
  lockCfbScheduleGame,
  LscApiError,
  updateCfbScheduleGame,
} from '../api';
import ErrorBanner from '../components/ErrorBanner';
import CfbInlineTeamChip from '../components/CfbInlineTeamChip';
import LoadingState from '../components/LoadingState';
import {
  formatCfbScheduleDateTime,
  formatCfbScheduleStatus,
  resolveEffectiveDynastySeasonId,
  validateCfbScheduleGameInput,
} from '../cfbScheduleFormUtils';
import type { CfbScheduleGame } from '../cfbScheduleTypes';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import { lscPath, lscReturnTo } from '../routing';

const EMPTY_FORM = {
  weekNumber: '1',
  homeAssignmentId: '',
  awayAssignmentId: '',
  scheduledAt: '',
  notes: '',
};

const LscCfbSchedulePage: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/cfb/schedule`)}>
      <CfbScheduleContent leagueId={leagueId ?? ''} />
    </LscAuthGate>
  );
};

function CfbScheduleContent({ leagueId }: { leagueId: string }) {
  const { league, isCommissioner } = useLeague();
  const featureEnabled = isLscCfbDynastySetupEnabled();
  const canMutate = Boolean(isCommissioner);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dynastySeasonId, setDynastySeasonId] = useState<string>('');
  const [seasons, setSeasons] = useState<Array<{ id: string; display_name: string }>>([]);
  const [season, setSeason] = useState<{ id: string; display_name: string } | null>(null);
  const [assignments, setAssignments] = useState<
    Array<{ id: string; option_label?: string; school_name?: string; member_display_label?: string }>
  >([]);
  const [games, setGames] = useState<CfbScheduleGame[]>([]);
  const [activeAssignmentCount, setActiveAssignmentCount] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingGameId, setEditingGameId] = useState<string | null>(null);
  const [weekFilter, setWeekFilter] = useState<string>('');

  const setupPath = lscPath(`/leagues/${leagueId}/cfb/setup`);
  const intakePath = lscPath(`/leagues/${leagueId}/cfb/schedule/intake`);
  const visionIntakeEnabled = isLscCfbVisionScheduleIntakeEnabled();
  const vodIntakeEnabled = isLscCfbVodScheduleIntakeEnabled();

  const loadSchedule = useCallback(async (selectedSeasonId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCfbSchedule(leagueId, selectedSeasonId || undefined);
      setDynastySeasonId(result.dynasty_season_id ?? result.season?.id ?? '');
      setSeasons(result.seasons ?? []);
      setSeason(result.season ?? null);
      setAssignments(result.assignments ?? []);
      setGames(result.games ?? []);
      setActiveAssignmentCount(result.active_assignment_count ?? 0);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not load CFB schedule.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    if (featureEnabled && league?.game_key === 'cfb') {
      void loadSchedule();
    } else {
      setLoading(false);
    }
  }, [featureEnabled, league?.game_key, loadSchedule]);

  const filteredGames = useMemo(() => {
    if (!weekFilter.trim()) {
      return games;
    }
    const week = Number(weekFilter);
    if (!Number.isFinite(week)) {
      return games;
    }
    return games.filter((game) => Number(game.week_number) === week);
  }, [games, weekFilter]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingGameId(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canMutate) {
      return;
    }
    const effectiveSeasonId = resolveEffectiveDynastySeasonId(dynastySeasonId, season);
    const weekNumber = Number(form.weekNumber);
    const validationError = validateCfbScheduleGameInput({
      dynastySeasonId: effectiveSeasonId,
      weekNumber,
      homeAssignmentId: form.homeAssignmentId,
      awayAssignmentId: form.awayAssignmentId,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (editingGameId) {
        await updateCfbScheduleGame(leagueId, editingGameId, {
          week_number: weekNumber,
          home_dynasty_assignment_id: form.homeAssignmentId,
          away_dynasty_assignment_id: form.awayAssignmentId,
          scheduled_at: form.scheduledAt.trim() || undefined,
          notes: form.notes.trim() || undefined,
        });
      } else {
        await createCfbScheduleGame(leagueId, {
          dynasty_season_id: effectiveSeasonId,
          week_number: weekNumber,
          home_dynasty_assignment_id: form.homeAssignmentId,
          away_dynasty_assignment_id: form.awayAssignmentId,
          scheduled_at: form.scheduledAt.trim() || undefined,
          notes: form.notes.trim() || undefined,
        });
      }
      resetForm();
      await loadSchedule(effectiveSeasonId);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not save schedule game.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (game: CfbScheduleGame) => {
    setEditingGameId(game.id);
    setForm({
      weekNumber: String(game.week_number ?? 1),
      homeAssignmentId: game.home_dynasty_assignment_id ?? '',
      awayAssignmentId: game.away_dynasty_assignment_id ?? '',
      scheduledAt: game.scheduled_at ? game.scheduled_at.slice(0, 16) : '',
      notes: game.notes ?? '',
    });
    setError(null);
  };

  const handleDelete = async (gameId: string) => {
    if (!canMutate) return;
    setSaving(true);
    setError(null);
    try {
      await deleteCfbScheduleGame(leagueId, gameId);
      if (editingGameId === gameId) {
        resetForm();
      }
      await loadSchedule(dynastySeasonId);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not delete schedule game.');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (gameId: string) => {
    if (!canMutate) return;
    setSaving(true);
    setError(null);
    try {
      await approveCfbScheduleGame(leagueId, gameId);
      await loadSchedule(dynastySeasonId);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not approve schedule game.');
    } finally {
      setSaving(false);
    }
  };

  const handleLock = async (gameId: string) => {
    if (!canMutate) return;
    setSaving(true);
    setError(null);
    try {
      await lockCfbScheduleGame(leagueId, gameId);
      await loadSchedule(dynastySeasonId);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not lock schedule game.');
    } finally {
      setSaving(false);
    }
  };

  if (!featureEnabled || league?.game_key !== 'cfb') {
    return (
      <section className="card p-6" data-testid="cfb-schedule-disabled">
        <h1 className="text-xl font-semibold text-white">CFB Schedule</h1>
        <p className="mt-2 text-secondary-300 text-sm">CFB Dynasty Setup is not enabled for this league.</p>
      </section>
    );
  }

  if (loading) {
    return <LoadingState message="Loading CFB schedule..." />;
  }

  return (
    <div className="space-y-6" data-testid="cfb-schedule-page">
      <section className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white">CFB Schedule</h1>
            <p className="text-secondary-300 text-sm">
              Create and approve the weekly schedule for this dynasty season.
            </p>
          </div>
          {canMutate && (visionIntakeEnabled || vodIntakeEnabled) ? (
            <div className="flex flex-wrap gap-2">
              {visionIntakeEnabled ? (
                <Link
                  to={intakePath}
                  className="btn-secondary whitespace-nowrap"
                  data-testid="cfb-schedule-import-screenshots"
                >
                  Import from screenshots
                </Link>
              ) : null}
              {vodIntakeEnabled ? (
                <Link
                  to={intakePath}
                  className="btn-secondary whitespace-nowrap"
                  data-testid="cfb-schedule-import-vod"
                >
                  Import from Twitch VOD
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>

      {error ? <ErrorBanner message={error} /> : null}

      {!season ? (
        <section className="card p-6 space-y-3" data-testid="cfb-schedule-no-season">
          <h2 className="text-lg font-semibold text-white">Build your CFB dynasty schedule.</h2>
          <p className="text-secondary-300 text-sm">
            Create weekly matchups from your approved school assignments. Approved games become your
            league&apos;s schedule truth.
          </p>
          {canMutate ? (
            <Link to={setupPath} className="btn-primary inline-block">
              Go to CFB Setup
            </Link>
          ) : null}
        </section>
      ) : (
        <>
          <section className="card p-6 space-y-4" data-testid="cfb-schedule-season-context">
            <div className="flex max-w-lsc-narrow flex-wrap gap-4 items-end">
              <label className="block">
                <span className="text-sm text-secondary-300">Dynasty season</span>
                {seasons.length > 1 ? (
                  <select
                    className="input-field mt-1"
                    value={dynastySeasonId}
                    onChange={(e) => {
                      void loadSchedule(e.target.value);
                    }}
                    data-testid="cfb-schedule-season-select"
                  >
                    {seasons.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.display_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-1 text-white" data-testid="cfb-schedule-season-name">
                    {season.display_name}
                  </p>
                )}
              </label>
              <label className="block">
                <span className="text-sm text-secondary-300">Filter week</span>
                <input
                  className="input-field mt-1 w-28"
                  type="number"
                  min={1}
                  value={weekFilter}
                  onChange={(e) => setWeekFilter(e.target.value)}
                  placeholder="All"
                  data-testid="cfb-schedule-week-filter"
                />
              </label>
            </div>
          </section>

          {activeAssignmentCount < 2 ? (
            <section className="card p-6 space-y-3" data-testid="cfb-schedule-insufficient-assignments">
              <p className="text-secondary-300 text-sm">
                {activeAssignmentCount === 0
                  ? 'Add school assignments before building your schedule.'
                  : 'At least two school assignments are needed to create a game.'}
              </p>
              {canMutate ? (
                <Link to={setupPath} className="btn-secondary inline-block">
                  Go to CFB Setup
                </Link>
              ) : null}
            </section>
          ) : null}

          {canMutate && activeAssignmentCount >= 2 ? (
            <section className="card max-w-lsc-narrow p-6 space-y-4" data-testid="cfb-schedule-add-game">
              <h2 className="text-lg font-semibold text-white">
                {editingGameId ? 'Edit draft game' : 'Add game'}
              </h2>
              <form className="grid gap-3 md:grid-cols-2" onSubmit={handleSubmit}>
                <label className="block">
                  <span className="text-sm text-secondary-300">Week number</span>
                  <input
                    className="input-field mt-1 w-full"
                    type="number"
                    min={1}
                    required
                    value={form.weekNumber}
                    onChange={(e) => setForm({ ...form, weekNumber: e.target.value })}
                    data-testid="cfb-schedule-week-input"
                  />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm text-secondary-300">Home school assignment</span>
                  <select
                    className="input-field mt-1 w-full"
                    required
                    value={form.homeAssignmentId}
                    onChange={(e) => setForm({ ...form, homeAssignmentId: e.target.value })}
                    data-testid="cfb-schedule-home-assignment"
                  >
                    <option value="">Select home assignment</option>
                    {assignments.map((assignment) => (
                      <option key={assignment.id} value={assignment.id}>
                        {assignment.option_label ?? assignment.school_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm text-secondary-300">Away school assignment</span>
                  <select
                    className="input-field mt-1 w-full"
                    required
                    value={form.awayAssignmentId}
                    onChange={(e) => setForm({ ...form, awayAssignmentId: e.target.value })}
                    data-testid="cfb-schedule-away-assignment"
                  >
                    <option value="">Select away assignment</option>
                    {assignments.map((assignment) => (
                      <option key={assignment.id} value={assignment.id}>
                        {assignment.option_label ?? assignment.school_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm text-secondary-300">Scheduled date/time (optional)</span>
                  <input
                    className="input-field mt-1 w-full"
                    type="datetime-local"
                    value={form.scheduledAt}
                    onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
                    data-testid="cfb-schedule-datetime-input"
                  />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm text-secondary-300">Notes (optional)</span>
                  <input
                    className="input-field mt-1 w-full"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="cfb-schedule-notes-input"
                  />
                </label>
                <div className="md:col-span-2 flex flex-wrap gap-2">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={saving}
                    data-testid="cfb-schedule-save-button"
                  >
                    {saving ? 'Saving…' : editingGameId ? 'Save changes' : 'Add game'}
                  </button>
                  {editingGameId ? (
                    <button type="button" className="btn-secondary" onClick={resetForm}>
                      Cancel edit
                    </button>
                  ) : null}
                </div>
              </form>
            </section>
          ) : null}

          <section className="card p-6 space-y-4" data-testid="cfb-schedule-table-section">
            <h2 className="text-lg font-semibold text-white">Schedule games</h2>
            {filteredGames.length === 0 ? (
              <p className="text-secondary-400 text-sm" data-testid="cfb-schedule-empty-games">
                Build your CFB dynasty schedule. Create weekly matchups from your approved school
                assignments. Approved games become your league&apos;s schedule truth.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="cfb-schedule-table">
                  <thead>
                    <tr className="text-secondary-400 text-left">
                      <th className="py-2 pr-4">Week</th>
                      <th className="py-2 pr-4">Home</th>
                      <th className="py-2 pr-4">Away</th>
                      <th className="py-2 pr-4">Members</th>
                      <th className="py-2 pr-4">Scheduled time</th>
                      <th className="py-2 pr-4">Status</th>
                      {canMutate ? <th className="py-2 pr-4">Actions</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGames.map((game) => (
                      <tr key={game.id} className="border-t border-secondary-800">
                        <td className="py-2 pr-4">{game.week_number}</td>
                        <td
                          className="py-2 pr-4"
                          data-testid={`cfb-schedule-home-${game.id}`}
                        >
                          {game.home_school_name ? (
                            <CfbInlineTeamChip
                              label={game.home_school_name}
                              showLabel
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td
                          className="py-2 pr-4"
                          data-testid={`cfb-schedule-away-${game.id}`}
                        >
                          {game.away_school_name ? (
                            <CfbInlineTeamChip
                              label={game.away_school_name}
                              showLabel
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td
                          className="py-2 pr-4"
                          data-testid={`cfb-schedule-members-${game.id}`}
                        >
                          {game.members_summary}
                        </td>
                        <td className="py-2 pr-4">{formatCfbScheduleDateTime(game.scheduled_at)}</td>
                        <td
                          className="py-2 pr-4"
                          data-testid={`cfb-schedule-status-${game.id}`}
                        >
                          {formatCfbScheduleStatus(game.approval_status)}
                        </td>
                        {canMutate ? (
                          <td className="py-2 pr-4 space-x-2 whitespace-nowrap">
                            {game.approval_status === 'draft' ? (
                              <>
                                <button
                                  type="button"
                                  className="text-primary-300 hover:text-primary-200"
                                  onClick={() => handleEdit(game)}
                                  data-testid={`cfb-schedule-edit-${game.id}`}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="text-red-300 hover:text-red-200"
                                  onClick={() => void handleDelete(game.id)}
                                  data-testid={`cfb-schedule-delete-${game.id}`}
                                >
                                  Delete
                                </button>
                                <button
                                  type="button"
                                  className="text-green-300 hover:text-green-200"
                                  onClick={() => void handleApprove(game.id)}
                                  data-testid={`cfb-schedule-approve-${game.id}`}
                                >
                                  Approve
                                </button>
                              </>
                            ) : null}
                            {game.approval_status === 'approved' ? (
                              <button
                                type="button"
                                className="text-amber-200 hover:text-amber-100"
                                onClick={() => void handleLock(game.id)}
                                data-testid={`cfb-schedule-lock-${game.id}`}
                              >
                                Lock
                              </button>
                            ) : null}
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default LscCfbSchedulePage;
